import type { UserResource } from "@exonn/js-sdk";
import type { StorageKey } from "@exonn/storage";
import { getUserDataPayload } from "src/user-data/getUserDataPayload.ts";
import { getUserDataCache } from "src/user-data/cache/getUserDataCache.ts";
import zod, { type ZodType } from "zod";
import { logger } from "src/logger";

const defaultSanitazor = <V = any>(data: V) => data;

const defaultValidationSchema = zod.any();

export function getUserDataState<V = any, D extends V = V>(options: {
  user: UserResource;
  sanitaze?: (data: V) => void;
  validationSchema?: ZodType;
  key: StorageKey;
  defaultValue: D;
}): V;
export function getUserDataState<V = any, D extends V = V>(options: {
  user: UserResource;
  key: StorageKey;
  validationSchema?: ZodType;
  sanitaze?: (data: V) => V;
  defaultValue?: D;
}): V | undefined;
export function getUserDataState<V = any, D extends V = V>(options: {
  user: UserResource;
  key: StorageKey;
  validationSchema?: ZodType;
  sanitaze?: (data: V) => V;
  defaultValue?: D;
}): V | undefined {
  const {
    user,
    key,
    defaultValue,
    sanitaze = defaultSanitazor,
    validationSchema = defaultValidationSchema,
  } = options;
  const cachedValue = getUserDataCache<V>({
    user,
    key,
  });

  if (cachedValue != null) {
    return cachedValue;
  }

  const databaseValue = getUserDataPayload<V>(user, key);

  if (databaseValue) {
    const result = validationSchema.safeParse(databaseValue);
    if (!result.success) {
      logger.error(result.error, {
        key,
        userId: user.id,
      });
      return defaultValue;
    }

    try {
      return sanitaze(databaseValue);
    } catch (error: any) {
      logger.error(error, {
        key,
        userId: user.id,
      });
      return defaultValue;
    }
  }

  return defaultValue;
}
